import React, { useEffect, useState } from "react";
import { useAuth } from "@/auth";
import { useI18n } from "@/i18n";
import { toast } from "sonner";
import { FileText, Wallet, TrendingDown, ChevronDown, ChevronUp, Package } from "lucide-react";
import { fmtMoney } from "@/lib/money";
import { PdfLangMenu } from "@/components/PdfLangMenu";
import { invoicePDF } from "@/lib/invoicePdf";
import { statementPDF } from "@/lib/statementPdf";

export default function MyInvoices() {
  const { api } = useAuth();
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [statement, setStatement] = useState(null);
  const [settings, setSettings] = useState({});
  const [open, setOpen] = useState(null);

  useEffect(() => {
    api.get("/my/invoices").then(r => setData(r.data)).catch((err) => toast.error(err.response?.data?.detail || "Error"));
    api.get("/my/statement").then(r => setStatement(r.data)).catch(() => {});
    api.get("/settings/invoice").then(r => setSettings(r.data || {})).catch(() => {});
    // eslint-disable-next-line
  }, []);

  const cur = data?.moneda || "EUR";
  const money = (v, m) => fmtMoney(v, m || cur);

  return (
    <div className="space-y-5" data-testid="my-invoices-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-lg font-semibold flex items-center gap-2">
            <FileText className="w-5 h-5 text-indigo-600" /> {t("my_invoices")}
          </h2>
          <p className="text-sm text-slate-500">{t("my_invoices_hint")}</p>
        </div>
        {statement && (
          <PdfLangMenu testid="my-statement-pdf" onPrint={(lg) => statementPDF(statement, lg, settings)} label={t("download_statement")} />
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-4" data-testid="my-invoiced">
          <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-indigo-700"><FileText className="w-3.5 h-3.5" /> {t("invoiced")}</div>
          <div className="mt-1.5 text-2xl font-semibold tabular-nums text-indigo-900">{money(data?.facturado || 0)}</div>
        </div>
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4" data-testid="my-paid">
          <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-emerald-700"><Wallet className="w-3.5 h-3.5" /> {t("paid")}</div>
          <div className="mt-1.5 text-2xl font-semibold tabular-nums text-emerald-900">{money(data?.pagado || 0)}</div>
        </div>
        <div className="rounded-xl border border-slate-900 bg-slate-900 p-4 text-white" data-testid="my-balance">
          <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider opacity-70"><TrendingDown className="w-3.5 h-3.5" /> {t("balance")}</div>
          <div className="mt-1.5 text-2xl font-semibold tabular-nums">{money(data?.saldo || 0)}</div>
        </div>
      </div>

      {(data?.invoices || []).length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-14 text-center text-slate-400">
          <FileText className="w-12 h-12 mx-auto opacity-30 mb-3" />
          <p className="text-sm">{t("no_invoices_yet")}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {data.invoices.map(i => (
            <div key={i.id} className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={`my-inv-${i.numero}`}>
              <div className="p-4 flex flex-wrap items-center gap-3 justify-between">
                <div className="min-w-0">
                  <div className="font-mono font-semibold text-sm">{i.numero}</div>
                  <div className="text-[11px] text-slate-500 tabular-nums">
                    {i.fecha}{i.vencimiento ? ` · ${t("due_date")}: ${i.vencimiento}` : ""}
                  </div>
                </div>
                <div className="flex items-center gap-4 flex-wrap">
                  <div className="text-end">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">{t("total")}</div>
                    <div className="font-semibold tabular-nums">{money(i.total, i.moneda)}</div>
                  </div>
                  <div className="text-end">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">{t("balance")}</div>
                    <div className="font-semibold tabular-nums">{money(i.saldo, i.moneda)}</div>
                  </div>
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${i.estado === "pagada" ? "bg-emerald-100 text-emerald-700" : i.estado === "parcial" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>
                    {t(`inv_status_${i.estado}`) || i.estado}
                  </span>
                  <PdfLangMenu testid={`my-inv-pdf-${i.numero}`} onPrint={(lg) => invoicePDF(i, lg, i.moneda || cur, settings)} label="PDF" />
                  <button data-testid={`my-inv-toggle-${i.numero}`} onClick={() => setOpen(open === i.id ? null : i.id)} className="p-1.5 text-slate-400 hover:text-indigo-600 rounded">
                    {open === i.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {open === i.id && (
                <div className="border-t border-slate-100 bg-slate-50/60 p-4 space-y-3" data-testid={`my-inv-detail-${i.numero}`}>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs min-w-[420px]">
                      <thead className="text-[10px] uppercase text-slate-400">
                        <tr><th className="p-2 text-start">{t("description")}</th><th className="p-2 text-end">{t("qty")}</th><th className="p-2 text-end">{t("unit_price")}</th><th className="p-2 text-end">{t("total")}</th></tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200">
                        {(i.lineas || []).map((l, idx) => (
                          <tr key={l._uid || l.id || `${i.id}-line-${idx}`}>
                            <td className="p-2">{l.descripcion}</td>
                            <td className="p-2 text-end tabular-nums">{l.cantidad}</td>
                            <td className="p-2 text-end tabular-nums">{money(l.precio_unitario, i.moneda)}</td>
                            <td className="p-2 text-end tabular-nums font-semibold">{money((+l.cantidad || 0) * (+l.precio_unitario || 0), i.moneda)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {i.shipment && (
                    <div className="rounded-lg border border-slate-200 bg-white p-3 text-xs flex flex-wrap items-center gap-3">
                      <Package className="w-4 h-4 text-red-600" />
                      <span className="text-slate-400 uppercase text-[10px] tracking-wider">{t("linked_shipment")}</span>
                      <b className="font-mono">{i.shipment.tracking_code}</b>
                      <span className="text-slate-500">{i.shipment.origen} → {i.shipment.destino}</span>
                      <span className="text-slate-500 tabular-nums">{i.shipment.peso} kg · {i.shipment.cbm} CBM</span>
                    </div>
                  )}
                  {(i.pagos || []).length > 0 && (
                    <div className="text-xs">
                      <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">{t("acc_tab_payments")}</div>
                      {i.pagos.map(p => (
                        <div key={p.id} className="flex justify-between border-b border-slate-200 py-1">
                          <span className="tabular-nums text-slate-600">{p.fecha} · {t(`pay_${p.metodo}`) || p.metodo}</span>
                          <b className="tabular-nums text-emerald-700">{money(p.monto, p.moneda || i.moneda)}</b>
                        </div>
                      ))}
                    </div>
                  )}
                  {i.remarks && <p className="text-[11px] text-slate-500 italic">{i.remarks}</p>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
